import React from 'react';
import { Helmet } from 'react-helmet-async';
import { sanitizeForMeta, sanitizeStructuredData, sanitizeUrl } from '../utils/security';

export interface SEOProps {
  /**
   * Page title (will be appended with " | Local Artist Fairs")
   */
  title: string;

  /**
   * Meta description for search engines
   */
  description: string;

  /**
   * Keywords for search engines (optional)
   */
  keywords?: string;

  /**
   * Canonical URL for this page (absolute URL)
   */
  canonicalUrl?: string;

  /**
   * Open Graph image URL (absolute URL)
   */
  ogImage?: string;

  /**
   * Open Graph type (default: "website")
   */
  ogType?: 'website' | 'article' | 'event' | 'business.business';

  /**
   * Twitter card type (default: "summary_large_image")
   */
  twitterCard?: 'summary' | 'summary_large_image' | 'app' | 'player';

  /**
   * Additional structured data (Schema.org JSON-LD)
   */
  structuredData?: object | object[];
}

/**
 * SEOHead Component
 *
 * Provides comprehensive SEO meta tags including:
 * - Page title and description
 * - Open Graph tags (Facebook, LinkedIn)
 * - Twitter Card tags
 * - Canonical URLs
 * - Structured data (Schema.org JSON-LD)
 *
 * Usage:
 * ```tsx
 * <SEOHead
 *   title="Artist Fairs Near You"
 *   description="Discover local artist fairs, craft shows, and markets"
 *   canonicalUrl="https://localartistfairs.com/"
 *   ogImage="https://localartistfairs.com/og-image.jpg"
 * />
 * ```
 */
const SEOHead: React.FC<SEOProps> = ({
  title,
  description,
  keywords,
  canonicalUrl,
  ogImage,
  ogType = 'website',
  twitterCard = 'summary_large_image',
  structuredData
}) => {
  // Sanitize all inputs for security
  const safeTitle = sanitizeForMeta(title, 200);
  const safeDescription = sanitizeForMeta(description, 160);
  const safeKeywords = keywords ? sanitizeForMeta(keywords, 200) : undefined;
  const safeCanonicalUrl = canonicalUrl ? sanitizeUrl(canonicalUrl) : window.location.href;
  const defaultOgImage = ogImage || `${window.location.origin}/og-default.jpg`;
  const safeOgImage = sanitizeUrl(defaultOgImage);

  // Build full title
  const siteName = 'Local Artist Fairs';
  const fullTitle = `${safeTitle} | ${siteName}`;

  // Sanitize structured data
  const safeStructuredData = structuredData ? sanitizeStructuredData(structuredData) : undefined;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={safeDescription} />
      {safeKeywords && <meta name="keywords" content={safeKeywords} />}

      {/* Canonical URL */}
      {safeCanonicalUrl && <link rel="canonical" href={safeCanonicalUrl} />}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={safeCanonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={safeDescription} />
      <meta property="og:image" content={safeOgImage} />
      <meta property="og:site_name" content={siteName} />

      {/* Twitter */}
      <meta property="twitter:card" content={twitterCard} />
      <meta property="twitter:url" content={safeCanonicalUrl} />
      <meta property="twitter:title" content={fullTitle} />
      <meta property="twitter:description" content={safeDescription} />
      <meta property="twitter:image" content={safeOgImage} />

      {/* Structured Data (Schema.org JSON-LD) */}
      {safeStructuredData && (
        <script type="application/ld+json">
          {JSON.stringify(
            Array.isArray(safeStructuredData)
              ? { '@graph': safeStructuredData }
              : safeStructuredData
          )}
        </script>
      )}
    </Helmet>
  );
};

export default SEOHead;
